// components/TemplateSelectorModal.jsx
// 🎨 USER INTERFACE FOR CHOOSING MEME TEMPLATE

import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { X, Download, Check } from 'lucide-react';
import html2canvas from 'html2canvas';
import { TEMPLATES, selectBestTemplate } from './VerdictMemeTemplates';

export default function TemplateSelectorModal({ trial, verdict, onClose }) {
  // Auto-select best template based on content
  const recommendedTemplate = selectBestTemplate(trial, verdict);
  const [selectedTemplate, setSelectedTemplate] = useState(recommendedTemplate);
  const [format, setFormat] = useState('square'); // 'square' or 'story'
  const [downloading, setDownloading] = useState(false);
  
  const canvasRef = useRef(null);

  // Get the selected template component
  const SelectedComponent = TEMPLATES[selectedTemplate].component;

  const handleDownload = async (templateKey) => {
    setDownloading(true);
    
    try {
      // Temporarily render the template in DOM
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      document.body.appendChild(tempContainer);
      
      // Render the template
      const Component = TEMPLATES[templateKey || selectedTemplate].component;
      const root = ReactDOM.createRoot(tempContainer);
      
      await new Promise((resolve) => {
        root.render(
          React.createElement(Component, {
            trial,
            verdict,
            format
          })
        );
        setTimeout(resolve, 100); // Give it time to render
      });
      
      // Capture with html2canvas
      const element = tempContainer.firstChild;
      const canvas = await html2canvas(element, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      // Download
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `gweffit_verdict_${templateKey || selectedTemplate}_${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Cleanup
      root.unmount();
      document.body.removeChild(tempContainer);
      
    } catch (error) {
      console.error('Error downloading meme:', error);
      alert('Failed to download. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAll = async () => {
    setDownloading(true);
    
    for (const [key] of Object.entries(TEMPLATES)) {
      await handleDownload(key);
      await new Promise(resolve => setTimeout(resolve, 500)); // Delay between downloads
    }
    
    setDownloading(false);
    alert('All templates downloaded! 🎉');
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-y-auto">
      <div className="bg-slate-800 rounded-3xl max-w-7xl w-full max-h-[95vh] overflow-y-auto shadow-2xl border-2 border-purple-500">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 px-8 py-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-3xl font-black text-white mb-2">Choose Your Meme Style</h2>
            <p className="text-slate-400">
              Pick your favorite template or download them all!
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-3 hover:bg-slate-700 rounded-xl transition-colors"
          >
            <X className="w-6 h-6 text-slate-400" />
          </button>
        </div>

        {/* Format toggle */}
        <div className="px-8 py-4 border-b border-slate-700">
          <div className="flex items-center gap-4">
            <span className="text-slate-300 font-semibold">Format:</span>
            <button
              onClick={() => setFormat('square')}
              className={`px-6 py-2 rounded-xl font-bold transition-all ${
                format === 'square'
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              📱 Square (1080x1080)
            </button>
            <button
              onClick={() => setFormat('story')}
              className={`px-6 py-2 rounded-xl font-bold transition-all ${
                format === 'story'
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              📲 Story (1080x1920)
            </button>
          </div>
        </div>

        {/* Template grid */}
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(TEMPLATES).map(([key, template]) => {
              const isSelected = selectedTemplate === key;
              const isRecommended = key === recommendedTemplate;
              
              return (
                <div
                  key={key}
                  onClick={() => setSelectedTemplate(key)}
                  className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all transform hover:scale-105 ${
                    isSelected
                      ? 'ring-4 ring-purple-500 shadow-2xl'
                      : 'ring-2 ring-slate-700 hover:ring-slate-600'
                  }`}
                >
                  {/* Recommended badge */}
                  {isRecommended && (
                    <div className="absolute top-4 right-4 z-10 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                      ⭐ Recommended
                    </div>
                  )}

                  {/* Selected checkmark */}
                  {isSelected && (
                    <div className="absolute top-4 left-4 z-10 bg-purple-600 text-white rounded-full p-2 shadow-lg">
                      <Check className="w-5 h-5" />
                    </div>
                  )}

                  {/* Template preview */}
                  <div className="aspect-square bg-slate-900 flex items-center justify-center p-4">
                    <div className="text-center">
                      <div className="text-6xl mb-3">{template.icon}</div>
                      <div className="text-xl font-black text-white mb-2">
                        {template.name}
                      </div>
                      <div className="text-sm text-slate-400">
                        {template.description}
                      </div>
                    </div>
                  </div>

                  {/* Template info */}
                  <div className="bg-slate-900 p-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(key);
                      }}
                      disabled={downloading}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-bold transition-all ${
                        isSelected
                          ? 'bg-purple-600 hover:bg-purple-500 text-white'
                          : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Download className="w-4 h-4" />
                      <span>Download This</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action buttons */}
        <div className="sticky bottom-0 bg-slate-800 border-t border-slate-700 px-8 py-6 flex items-center justify-between">
          <div className="text-slate-400 text-sm">
            Tip: Download all styles and test which gets the most shares! 🚀
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={handleDownloadAll}
              disabled={downloading}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {downloading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  <span>Download All 5</span>
                </>
              )}
            </button>
            
            <button
              onClick={onClose}
              className="px-6 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-white font-bold transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Hidden render target for selected template */}
      <div ref={canvasRef} style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <SelectedComponent trial={trial} verdict={verdict} format={format} />
      </div>
    </div>
  );
}